import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, ChevronLeft, ChevronRight, Trash2, Send, Eye, Loader2, Heart } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/components/common/Avatar";
import { AuthImage } from "@/components/common/AuthImage";
import { api, API_BASE } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { timeAgo } from "@/lib/format";
import { bgCss } from "./storyBg";
import { cn } from "@/lib/utils";

const ORIGIN = API_BASE.replace(/\/api$/, "");
const abs = (u) => (!u ? u : /^(data:|https?:)/.test(u) ? u : `${ORIGIN}${u}`);
const TEXT_MS = 6000;
const QUICK_REACTIONS = ["❤️", "🔥", "😂", "👏", "😮", "💯"];

// Reactions live as DM replies carrying story_ref (no per-story reactions endpoint yet), so we
// derive them from the reactors' existing DMs — bounded to keep the insights panel lightweight.
async function fetchStoryReactions(storyId, viewers) {
  const chats = await api.listChats().catch(() => null);
  const items = Array.isArray(chats) ? chats : (chats?.items || chats?.chats || []);
  const convByUser = {};
  for (const c of items) {
    if (c?.type === "dm" && c?.other?.id) convByUser[c.other.id] = c.id;
  }
  const reactorUsers = (viewers || [])
    .map((v) => v.user || v)
    .filter((u) => u && convByUser[u.id])
    .slice(0, 25);
  const pairs = await Promise.all(
    reactorUsers.map(async (u) => {
      try {
        const msgs = await api.getMessages(convByUser[u.id]);
        const list = Array.isArray(msgs) ? msgs : (msgs?.items || msgs?.messages || []);
        const react = [...list].reverse().find(
          (m) => m?.story_ref?.story_id === storyId && (m.sender_id === u.id || m.sender?.id === u.id),
        );
        if (!react) return null;
        return [u.id, react.text || "💬"]; // e2ee reactions arrive with empty text
      } catch {
        return null;
      }
    }),
  );
  const byUser = {};
  for (const p of pairs) if (p) byUser[p[0]] = p[1];
  return byUser;
}

function StoryMedia({ story, onVideoDur }) {
  const [videoUrl, setVideoUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    if (story?.kind === "video") {
      setVideoUrl(null);
      api.storyVideoUrl(story.id).then((r) => { if (alive) setVideoUrl(abs(r?.url || r?.video_url || r)); }).catch(() => {});
    }
    return () => { alive = false; };
  }, [story?.id, story?.kind]);

  if (story?.kind === "text") {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 text-center" style={{ background: bgCss(story.bg) }}>
        <p className="text-2xl font-bold leading-snug text-white" data-testid="story-text-content">{story.text}</p>
      </div>
    );
  }
  if (story?.kind === "image") {
    // v1.3.4 (cc_3): prefer the signed image_url (?media=url) — inline base64 can die mid-transfer on cellular/web.
    const signed = story.image_url ? abs(story.image_url) : null;
    const raw = story.image ? abs(story.image) : null;
    const src = signed || raw;
    const needsAuth = !signed && raw && !/^(data:|https?:)/.test(story.image);
    return (
      <div className="flex h-full w-full items-center justify-center bg-black">
        {needsAuth ? (
          <AuthImage src={src} alt="" className="max-h-full max-w-full object-contain" testid="story-image-content" />
        ) : (
          <img src={src} alt="" className="max-h-full max-w-full object-contain" data-testid="story-image-content" />
        )}
      </div>
    );
  }
  // video
  return (
    <div className="flex h-full w-full items-center justify-center bg-black">
      {videoUrl ? (
        <video
          src={videoUrl}
          autoPlay
          playsInline
          controls={false}
          className="max-h-full max-w-full object-contain"
          data-testid="story-video-content"
          onLoadedMetadata={(e) => onVideoDur?.(e.currentTarget.duration)}
        />
      ) : (
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      )}
    </div>
  );
}

export function StoryViewer({ groups, startIndex = 0, onClose }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [gi, setGi] = useState(startIndex);
  const [si, setSi] = useState(0);
  const [reply, setReply] = useState("");
  const [showViewers, setShowViewers] = useState(false);
  const [paused, setPaused] = useState(false);
  const [bursts, setBursts] = useState([]);
  const videoDur = useRef(TEXT_MS);

  const group = groups[gi];
  const ownerId = group?.user?.id;
  const { data } = useQuery({
    queryKey: ["user-stories", ownerId],
    queryFn: () => api.userStories(ownerId),
    enabled: !!ownerId,
  });
  const owner = data?.user || group?.user;
  const stories = data?.stories || [];
  const story = stories[si];

  useEffect(() => { setSi(0); setShowViewers(false); }, [gi]);

  const nextGroup = useCallback(() => {
    if (gi < groups.length - 1) setGi((g) => g + 1);
    else onClose();
  }, [gi, groups.length, onClose]);

  const prevGroup = useCallback(() => {
    if (gi > 0) setGi((g) => g - 1);
  }, [gi]);

  const advance = useCallback(() => {
    if (si < stories.length - 1) setSi((s) => s + 1);
    else nextGroup();
  }, [si, stories.length, nextGroup]);

  const back = useCallback(() => {
    if (si > 0) setSi((s) => s - 1);
    else prevGroup();
  }, [si, prevGroup]);

  // mark viewed + auto-advance timer
  useEffect(() => {
    if (!story) return undefined;
    if (!story.mine && !story.seen) api.viewStory(story.id).catch(() => {});
    if (paused || showViewers) return undefined;
    const ms = story.kind === "video" ? (videoDur.current || 10000) : TEXT_MS;
    const t = setTimeout(advance, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id, paused, showViewers, advance]);

  // esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") advance(); if (e.key === "ArrowLeft") back(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, advance, back]);

  const del = async () => {
    if (!story) return;
    try {
      await api.deleteStory(story.id);
      toast.success("Story deleted");
      qc.invalidateQueries({ queryKey: ["stories-feed"] });
      qc.invalidateQueries({ queryKey: ["user-stories", ownerId] });
      if (stories.length <= 1) nextGroup(); else if (si >= stories.length - 1) setSi((s) => Math.max(0, s - 1));
    } catch { toast.error("Couldn't delete."); }
  };

  const sendReply = async () => {
    const t = reply.trim();
    if (!t || !story || !owner?.id) return;
    setReply("");
    try {
      const chat = await api.openChat(owner.id);
      const cid = chat?.id || chat?.conversation_id || chat?.chat_id;
      if (!cid) throw new Error("no chat");
      onClose();
      navigate(`/app/chats/${cid}`, { state: { storyReply: { text: t, ownerName: owner.display_name } } });
    } catch {
      // Fall back to a silent inline reply if the DM couldn't be opened.
      api.replyStory(story.id, t).then(() => toast.success("Reply sent")).catch(() => toast.error("Couldn't send reply."));
    }
  };

  // One-tap reaction — fires instantly into the DM (no typing, viewer stays open).
  const fireReaction = (emoji) => {
    if (!story) return;
    const id = Date.now() + Math.random();
    setBursts((b) => [...b, { id, emoji }]);
    setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1100);
    // cc_22: quick emoji lands on the story's reaction strip only (no DM); typed replies stay on /reply.
    api.reactStory(story.id, emoji).then(() => toast.success("Reaction sent")).catch((e) => {
      if (e?.response?.status === 400) toast.error("You can't react to your own story.");
      else toast.error("Couldn't send reaction.");
    });
  };

  const viewersQ = useQuery({
    queryKey: ["story-viewers", story?.id],
    queryFn: () => api.storyViewers(story.id),
    enabled: showViewers && !!story?.id && !!story?.mine,
  });

  // cc_8: viewer rows carry `reaction` + `reaction_e2ee` natively (1.3.4+). Fall back to the DM fan-out on older backends.
  const viewers = useMemo(() => viewersQ.data?.viewers || [], [viewersQ.data]);
  const nativeReactions = viewers.some((v) => "reaction" in v || "reaction_e2ee" in v);
  const reactionsQ = useQuery({
    queryKey: ["story-reactions", story?.id],
    queryFn: () => fetchStoryReactions(story.id, viewers),
    enabled: showViewers && !!story?.id && !!story?.mine && !!viewersQ.data && !nativeReactions && viewers.length > 0,
  });
  const reactionsByUser = useMemo(() => {
    if (nativeReactions) {
      const m = {};
      for (const v of viewers) {
        const u = v.user || v;
        if (v.reaction) m[u.id] = v.reaction;
        else if (v.reaction_e2ee) m[u.id] = "🔒";
      }
      return m;
    }
    return reactionsQ.data || {};
  }, [nativeReactions, viewers, reactionsQ.data]);
  const reactionsCount = Object.keys(reactionsByUser).length;

  // cc_10: live story reactions pushed to the owner's socket — update the insights panel without polling.
  const { subscribe } = useRealtime();
  useEffect(() => subscribe((evt) => {
    if (evt?.type !== "story.reaction" || !evt.story_id || !evt.user?.id) return;
    qc.setQueryData(["story-viewers", evt.story_id], (old) => {
      if (!old) return old;
      const viewers = [...(old.viewers || [])];
      const patch = { reaction: evt.e2ee ? null : (evt.reaction || null), reaction_e2ee: !!evt.e2ee };
      const idx = viewers.findIndex((v) => (v.user || v).id === evt.user.id);
      if (idx >= 0) viewers[idx] = { ...viewers[idx], ...patch };
      else viewers.unshift({ user: evt.user, at: new Date().toISOString(), ...patch });
      return { ...old, viewers, count: idx >= 0 ? old.count : (old.count || 0) + 1 };
    });
  }), [subscribe, qc]);

  const openDM = async (userId) => {
    try {
      const chat = await api.openChat(userId);
      const cid = chat?.id || chat?.conversation_id || chat?.chat_id;
      if (!cid) return;
      onClose();
      navigate(`/app/chats/${cid}`);
    } catch { /* ignore */ }
  };

  if (!group) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 backdrop-blur-sm" data-testid="story-viewer">
      <div className="relative flex h-full max-h-[860px] w-full max-w-[440px] flex-col overflow-hidden rounded-none bg-bloq-bg sm:h-[92vh] sm:rounded-3xl">
        {/* progress bars */}
        <div className="absolute inset-x-0 top-0 z-20 flex gap-1 p-2.5">
          {stories.map((s, i) => (
            <div key={s.id} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/25">
              <div className={cn("h-full bg-white transition-all", i < si ? "w-full" : i === si ? "w-full" : "w-0")} />
            </div>
          ))}
        </div>

        {/* header */}
        <div className="absolute inset-x-0 top-4 z-20 flex items-center gap-2.5 px-3 pt-2">
          <Avatar user={owner} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-white">{owner?.display_name}</div>
            <div className="text-[11px] text-white/70">{story ? timeAgo(story.created_at) : ""}</div>
          </div>
          {story?.mine && (
            <button onClick={del} className="flex h-8 w-8 items-center justify-center rounded-full bg-black/30 text-white/90 hover:text-destructive" data-testid="story-delete-btn" title="Delete story">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full bg-black/30 text-white" data-testid="story-close-btn">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* content */}
        <div
          className="relative flex-1"
          onMouseDown={() => setPaused(true)}
          onMouseUp={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          {story ? <StoryMedia story={story} onVideoDur={(d) => { videoDur.current = Math.max(3000, (d || 10) * 1000); }} /> : (
            <div className="flex h-full items-center justify-center text-white/60"><Loader2 className="h-6 w-6 animate-spin" /></div>
          )}
          {/* tap zones */}
          <button className="absolute inset-y-0 left-0 w-1/3" onClick={back} aria-label="Previous" data-testid="story-prev-zone" />
          <button className="absolute inset-y-0 right-0 w-1/3" onClick={advance} aria-label="Next" data-testid="story-next-zone" />
          {story?.text && story?.kind !== "text" && (
            <div className="pointer-events-none absolute inset-x-0 bottom-20 px-5 text-center">
              <span className="rounded-lg bg-black/45 px-2 py-1 text-sm font-medium text-white">{story.text}</span>
            </div>
          )}
        </div>

        {/* desktop arrows */}
        {gi > 0 && <button onClick={prevGroup} className="absolute -left-12 top-1/2 hidden -translate-y-1/2 text-white/70 hover:text-white sm:block"><ChevronLeft className="h-8 w-8" /></button>}
        {gi < groups.length - 1 && <button onClick={nextGroup} className="absolute -right-12 top-1/2 hidden -translate-y-1/2 text-white/70 hover:text-white sm:block"><ChevronRight className="h-8 w-8" /></button>}

        {/* footer */}
        <div className="absolute inset-x-0 bottom-0 z-20 p-3">
          {story?.mine ? (
            <div>
              <button
                onClick={() => setShowViewers((v) => !v)}
                className="flex items-center gap-2 rounded-full bg-black/45 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-md"
                data-testid="story-viewers-btn"
              >
                <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {story.view_count ?? 0}</span>
                {reactionsCount > 0 && (
                  <span className="flex items-center gap-1 text-bloq-mint" data-testid="story-insights-reactions-count">
                    <Heart className="h-3.5 w-3.5 fill-current" /> {reactionsCount}
                  </span>
                )}
                <span className="text-white/55">· {showViewers ? "Hide" : "Insights"}</span>
              </button>
              {showViewers && (
                <div className="mt-2 max-h-56 overflow-y-auto rounded-2xl border border-white/10 bg-bloq-s1/95 p-2" data-testid="story-viewers-list">
                  {viewersQ.isLoading ? (
                    <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-bloq-muted" /></div>
                  ) : (viewersQ.data?.viewers || []).length === 0 ? (
                    <div className="py-3 text-center text-xs text-bloq-muted">No views yet — share it around to kick off the loop.</div>
                  ) : (
                    (viewersQ.data.viewers || []).map((v) => {
                      const u = v.user || v;
                      const emoji = reactionsByUser[u.id];
                      return (
                        <button
                          key={u.id}
                          onClick={() => openDM(u.id)}
                          className="flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left transition-colors hover:bg-white/5"
                          data-testid={`story-viewer-${u.id}`}
                        >
                          <Avatar user={u} size="xs" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-white">{u.display_name}</div>
                            <div className="text-[11px] text-bloq-muted">viewed {timeAgo(v.at)}</div>
                          </div>
                          {emoji ? (
                            <span className="shrink-0 rounded-full bg-bloq-mint/15 px-2 py-0.5 text-base" data-testid={`story-viewer-reaction-${u.id}`}>{emoji}</span>
                          ) : (
                            <span className="shrink-0 text-[11px] text-white/40">Reply</span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3" data-testid="story-reply-area">
              {/* One-tap reactions — fire instantly into the DM, no typing */}
              <div
                className="relative flex items-center justify-center gap-3"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onTouchStart={() => setPaused(true)}
                data-testid="story-reactions-row"
              >
                <div className="pointer-events-none absolute inset-x-0 -top-10 flex justify-center gap-2">
                  {bursts.map((b) => (
                    <span key={b.id} className="text-4xl" style={{ animation: "storyReactFloat 1.1s ease-out forwards" }}>{b.emoji}</span>
                  ))}
                </div>
                {QUICK_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => fireReaction(emoji)}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-white/12 bg-white/10 text-2xl backdrop-blur-md transition-transform duration-150 hover:scale-125 hover:border-white/25 active:scale-90"
                    data-testid={`story-react-${emoji}`}
                    aria-label={`React ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>

              {/* Typed reply — opens the DM prefilled to continue the conversation */}
              <form onSubmit={(e) => { e.preventDefault(); sendReply(); }} className="flex items-center gap-2.5">
                <input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onFocus={() => setPaused(true)}
                  onBlur={() => setPaused(false)}
                  placeholder={`Reply to ${owner?.display_name?.split(" ")[0] || "story"}…`}
                  className="h-11 flex-1 rounded-full border border-white/20 bg-black/40 px-4 text-sm text-white placeholder:text-white/60 outline-none transition-colors focus:border-bloq-mint/50"
                  data-testid="story-reply-input"
                />
                <button type="submit" disabled={!reply.trim()} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bloq-gradient text-bloq-bg transition-opacity disabled:opacity-40" data-testid="story-reply-send">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
